import type { Member } from "./model";

/**
 * @mention autocomplete. The api resolves a handle to a member when it is their first name,
 * their name without spaces, or their email's local part, and only when exactly one member
 * matches (`people.resolveHandle`); the handle we insert is chosen to resolve to that person.
 */

const HANDLE_CHAR = /[\p{L}\p{N}._-]/u;

/** The `@partial` being typed at the caret, if any: where it starts and what follows the `@`. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  let i = caret;
  while (i > 0 && HANDLE_CHAR.test(text.charAt(i - 1))) i--;
  if (i === 0 || text.charAt(i - 1) !== "@") return null;
  const at = i - 1;
  if (at > 0 && !/\s/.test(text.charAt(at - 1))) return null; // e.g. an email address
  return { start: at, query: text.slice(i, caret) };
}

function firstName(m: Member): string | null {
  const first = m.name === null ? "" : m.name.trim().split(/\s+/)[0];
  return first === undefined || first.length === 0 ? null : first.toLowerCase();
}

function localPart(m: Member): string | null {
  return m.email === null ? null : (m.email.split("@")[0] ?? null);
}

function resolvesTo(handle: string, m: Member): boolean {
  const h = handle.toLowerCase();
  const name = m.name === null ? "" : m.name.toLowerCase();
  return firstName(m) === h || name.replace(/\s+/g, "") === h || localPart(m)?.toLowerCase() === h;
}

const VALID = /^[\p{L}\p{N}._-]+$/u;

/** A handle that resolves to exactly this member among `members`, or null if none does. */
export function handleFor(member: Member, members: readonly Member[]): string | null {
  const candidates = [firstName(member), member.name === null ? null : member.name.toLowerCase().replace(/\s+/g, ""), localPart(member)];
  for (const c of candidates) {
    if (c === null || c.length === 0 || !VALID.test(c)) continue;
    if (members.filter((m) => resolvesTo(c, m)).length === 1) return c;
  }
  return null;
}

/** Members whose name or email starts with the query (any word of the name), best first. */
export function matchMembers(members: readonly Member[], query: string, limit = 6): Member[] {
  const q = query.toLowerCase();
  const score = (m: Member): number => {
    const name = m.name === null ? "" : m.name.toLowerCase();
    const email = m.email === null ? "" : m.email.toLowerCase();
    if (name.startsWith(q)) return 0;
    if (name.split(/\s+/).some((w) => w.startsWith(q))) return 1;
    if (email.startsWith(q)) return 2;
    return -1;
  };
  return members
    .map((m) => ({ m, s: score(m) }))
    .filter((x) => x.s >= 0 && handleFor(x.m, members) !== null)
    .sort((a, b) => a.s - b.s || (a.m.name ?? "").localeCompare(b.m.name ?? ""))
    .slice(0, limit)
    .map((x) => x.m);
}

/** Replaces the `@partial` at `start` with `@handle ` and returns the new text and caret. */
export function applyMention(text: string, start: number, caret: number, handle: string): { text: string; caret: number } {
  const insert = `@${handle} `;
  return { text: text.slice(0, start) + insert + text.slice(caret), caret: start + insert.length };
}
